import * as React from 'react';
import { Modal } from './Modal';

export type ModalSheetFooterMode = 'cancel-only' | 'view' | 'edit' | 'add' | 'approver';

export interface ModalSheetFooterProps {
  mode: ModalSheetFooterMode;
  dirty?: boolean;
  confirmMessage?: string;
  actionLoading?: boolean;
  actionDisabled?: boolean;
  onAction?: () => void;
  onSecondaryAction?: () => void;
  actionLabel?: string;
  actionIcon?: string;
  showAction?: boolean;
  cancelLabel?: string;
  onCancel?: () => void;
  semanticId: string;
  /** Associates the action button with a `<form>` by its id (native `form` attribute). */
  actionForm?: string;
}

export function ModalSheetFooter({
  mode,
  dirty,
  confirmMessage,
  actionLoading,
  actionDisabled,
  onAction,
  onSecondaryAction,
  actionLabel,
  actionIcon,
  showAction = true,
  cancelLabel,
  onCancel,
  semanticId,
  actionForm,
}: ModalSheetFooterProps): React.ReactElement {
  switch (mode) {
    case 'cancel-only':
      return (
        <Modal.Footer>
          <Modal.Cancel
            dirty={dirty}
            confirmMessage={confirmMessage}
            label={cancelLabel}
            onClick={onCancel}
            semanticId={`${semanticId}Cancel`}
          />
          <span />
        </Modal.Footer>
      );

    case 'view':
      return (
        <Modal.Footer>
          <Modal.Cancel
            label={cancelLabel}
            onClick={onCancel}
            semanticId={`${semanticId}Cancel`}
          />
          {showAction && (
            <Modal.Action
              intent="edit"
              label={actionLabel}
              icon={actionIcon}
              onClick={onAction}
              loading={actionLoading}
              disabled={actionDisabled}
              semanticId={`${semanticId}Edit`}
            />
          )}
        </Modal.Footer>
      );

    case 'edit':
      return (
        <Modal.Footer>
          <Modal.Cancel
            dirty={dirty}
            confirmMessage={confirmMessage}
            label={cancelLabel}
            onClick={onCancel}
            semanticId={`${semanticId}Cancel`}
          />
          <Modal.Action
            intent="save"
            label={actionLabel}
            icon={actionIcon}
            onClick={onAction}
            loading={actionLoading}
            disabled={actionDisabled}
            form={actionForm}
            type={actionForm != null ? 'submit' : undefined}
            semanticId={`${semanticId}Save`}
          />
        </Modal.Footer>
      );

    case 'add':
      return (
        <Modal.Footer>
          <Modal.Cancel
            label={cancelLabel}
            onClick={onCancel}
            semanticId={`${semanticId}Cancel`}
          />
          <Modal.Action
            intent="add"
            label={actionLabel}
            icon={actionIcon}
            onClick={onAction}
            loading={actionLoading}
            disabled={actionDisabled}
            form={actionForm}
            type={actionForm != null ? 'submit' : undefined}
            semanticId={`${semanticId}Add`}
          />
        </Modal.Footer>
      );

    case 'approver':
      return (
        <Modal.Footer>
          <Modal.Action
            intent="reject"
            label={cancelLabel}
            onClick={onSecondaryAction}
            loading={actionLoading}
            semanticId={`${semanticId}Reject`}
          />
          <Modal.Action
            intent="approve"
            label={actionLabel}
            icon={actionIcon}
            onClick={onAction}
            loading={actionLoading}
            disabled={actionDisabled}
            semanticId={`${semanticId}Approve`}
          />
        </Modal.Footer>
      );
  }
}
